/**
 * MD-11 LibraryDrawer — localStorage-backed citation library (MVP, no server sync).
 *
 * Spec: docs/superpowers/specs/2026-05-11-chat-leftbar-source-upgrade-design.md §3.3
 * DECISIONS: s2026-05-11-CHAT-LEFTBAR-SOURCE-UPGRADE
 *
 * Key: `asklaw_library`. Cap: 100 item LRU. Newest first.
 * Defensive: catch quota, parse error → empty list, KHÔNG silent fail
 * (console.warn surfaced trong DevTools).
 */

import type { CitationRefShape } from '../../../src/workers/refusal/types';

export interface LibraryItem {
  id: string;                 // unique key, derived from doc_id + provision_path
  doc_id: string;
  doc_number?: string;
  doc_name?: string;
  provision_path: string;
  eff_status?: CitationRefShape['eff_status_at_render'];
  source_url?: string;
  saved_at: string;           // ISO
  source_tier?: CitationRefShape['source_tier'];
}

const KEY = 'asklaw_library';
const CAP = 100;

function safeWarn(msg: string, err?: unknown): void {
  if (typeof console !== 'undefined' && console.warn) {
    console.warn(`[library-store] ${msg}`, err);
  }
}

export function loadLibrary(): LibraryItem[] {
  try {
    if (typeof window === 'undefined') return [];
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is LibraryItem =>
        typeof x === 'object' &&
        x !== null &&
        typeof (x as LibraryItem).id === 'string' &&
        typeof (x as LibraryItem).doc_id === 'string'
    );
  } catch (e) {
    safeWarn('loadLibrary parse failed', e);
    return [];
  }
}

function saveLibrary(items: LibraryItem[]): void {
  try {
    if (typeof window === 'undefined') return;
    const capped = items.slice(0, CAP);
    window.localStorage.setItem(KEY, JSON.stringify(capped));
  } catch (e) {
    safeWarn('saveLibrary failed (quota or disabled)', e);
  }
}

export function libraryItemId(doc_id: string, provision_path: string): string {
  return `${doc_id}::${provision_path}`;
}

export function isInLibrary(doc_id: string, provision_path: string): boolean {
  const id = libraryItemId(doc_id, provision_path);
  return loadLibrary().some((x) => x.id === id);
}

export function addToLibrary(item: Omit<LibraryItem, 'id' | 'saved_at'>): LibraryItem[] {
  const id = libraryItemId(item.doc_id, item.provision_path);
  const now = new Date().toISOString();
  const newItem: LibraryItem = { ...item, id, saved_at: now };
  const list = loadLibrary().filter((x) => x.id !== id);
  list.unshift(newItem);
  saveLibrary(list);
  return list;
}

export function removeFromLibrary(id: string): LibraryItem[] {
  const list = loadLibrary().filter((x) => x.id !== id);
  saveLibrary(list);
  return list;
}

export function clearLibrary(): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(KEY);
  } catch (e) {
    safeWarn('clearLibrary failed', e);
  }
}

export const LIBRARY_CAP = CAP;
